import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  XMarkIcon,
  EyeIcon,
  StarIcon,
  TrashIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import addImage from "../../../../assets/add_image.png";
import classes from "./bulkSpareGallery.module.css";

export const BulkSpareGallery = ({
  value = [],
  onUpload,
  onDelete,
  onReplace,
  onPrimary,
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  /* ESC CLOSE PREVIEW */
  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && setPreviewImage(null);
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  /* DERIVED STATE */
  const uploadedCount = useMemo(
    () => value.filter((img) => img?.url).length,
    [value],
  );

  /* HANDLERS */
  const triggerUpload = useCallback(() => fileInputRef.current?.click(), []);

  

  const handleReplaceInternal = useCallback(
    (file, img) => {
      if (!file || !img) return;
      onReplace?.(file, img);
    },
    [onReplace],
  );

  return (
    <section className={classes.gallerySection}>
      {/* HEADER */}
      <div className={classes.galleryHead}>
        <h3 className={classes.galleryTitle}>Lot Photo Gallery</h3>
        <span className={classes.gallerySubtitle}>
          {uploadedCount} photo{uploadedCount !== 1 && "s"} uploaded
        </span>
      </div>

      {/* HIDDEN FILE INPUT */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={classes.hiddenInput}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload?.(file);
          // Clear the input value so the same file can be selected again if needed
          e.target.value = "";
        }}
      />

      {/* GALLERY LIST */}
      <div className={classes.galleryList}>
        {value.map((img) => {
          const isPrimary = img.is_thumbnail === 1;
          const isUploading = img.isUploading;

          return (
            <motion.article
              key={img.id} // ✅ always unique
              layout
              className={classes.galleryItem}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {/* IMAGE */}
              <div className={classes.imageWrapper}>
                <img src={img.url} className={classes.image} alt="" />
                {isUploading && (
                  <div className={classes.uploadOverlay}>
                    <div className={classes.progressContainer}>
                      <div className={classes.progressBar}>
                        <div
                          className={classes.progressFill}
                          style={{ width: `${img.progress || 0}%` }}
                        />
                      </div>
                      <div className={classes.progressText}>
                        Uploading {img.progress || 0}%
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ACTIONS */}
              {!isUploading && (
                <div className={classes.galleryActions}>
                  <div className={classes.actionsRight}>
                    <button
                      className={`${classes.actionBtn} ${classes.delete}`}
                      onClick={() => onDelete?.(img)}
                    >
                      <TrashIcon className={classes.icon} /> Delete
                    </button>
                    <button
                      className={`${classes.actionBtn} ${
                        isPrimary ? classes.primaryActive : classes.primary
                      }`}
                      onClick={() => onPrimary?.(img)}
                    >
                      <StarIcon className={classes.icon} /> Primary
                    </button>
                  </div>

                  <div className={classes.actionsLeft}>
                    <button
                      className={`${classes.actionBtn} ${classes.view}`}
                      onClick={() => setPreviewImage(img.url)}
                    >
                      <EyeIcon className={classes.icon} /> View
                    </button>

                    <label
                      className={`${classes.actionBtn} ${classes.replace}`}
                    >
                      <PhotoIcon className={classes.icon} /> Replace
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleReplaceInternal(file, img);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </motion.article>
          );
        })}

        {/* ADD TILE */}
        <motion.div
          layout
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          className={classes.addTile}
          onClick={triggerUpload}
        >
          <div className={classes.imageWrapper}>
            <img src={addImage} className={classes.image} alt="" />
          </div>
        </motion.div>
      </div>

      {/* PREVIEW MODAL */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            className={classes.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewImage(null)}
          >
            <motion.img
              src={previewImage}
              className={classes.modalImage}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className={classes.modalCloseBtn}
              onClick={() => setPreviewImage(null)}
            >
              <XMarkIcon className={classes.modalCloseIcon} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
